/* @jsx h */
import { defineComponent, h } from "vue";
import {
  groupUploadsAPI,
  uploadsAPI,
  type RestaurantAsset,
} from "@app/data/api/uploads";
import { AssetPicker } from "@app/vue/components/AssetPicker";
import type {
  WalletAssetRef,
  WalletBrandAssetSlot,
  WalletPassDesign,
  WalletPassDesignDocument,
} from "../../types";
import { PanelRow, PanelSection } from "./primitives";

/**
 * The three images a design owns. Each platform crops them to its own slots
 * (Apple icon/logo/strip/poster, Google logo/hero, the browser card), so one
 * high-resolution source per slot is enough. A plain pass leaves all empty.
 */
const SLOT_META: Record<
  WalletBrandAssetSlot,
  { label: string; size: string; hint: string; wide: boolean }
> = {
  icon: {
    label: "Icon",
    size: "≥ 114 × 114",
    hint: "Notifications and the lock screen. A default is used when empty.",
    wide: false,
  },
  logo: {
    label: "Logo",
    size: "≥ 480 × 150",
    hint: "Top-left on every platform. Transparent PNG works best.",
    wide: true,
  },
  artwork: {
    label: "Artwork",
    size: "≥ 1125 × 1344",
    hint: "Apple strip and iOS 27 poster, Google hero, and the browser card.",
    wide: true,
  },
};

const SLOTS: WalletBrandAssetSlot[] = ["icon", "logo", "artwork"];

/** Uploads from the designer land in the wallet collection in Settings → Assets. */
const UPLOAD_COLLECTION = "pass_images";

interface AssetsPanelProps {
  design: WalletPassDesign;
  scope?: "shop" | "group";
  onChange: (patch: Partial<WalletPassDesignDocument>) => void;
  /** Keeps the preview's URLs in step with a newly picked asset. */
  onAssetUrl: (assetId: string, url: string) => void;
}

export const AssetsPanel = defineComponent<AssetsPanelProps>({
  name: "AssetsPanel",
  props: ["design", "scope", "onChange", "onAssetUrl"],
  setup(props) {
    const brand = () => props.design.document.brand;
    const setSlot = (
      slot: WalletBrandAssetSlot,
      ref: WalletAssetRef | undefined,
    ) => {
      const assets = { ...brand().assets };
      if (ref) assets[slot] = ref;
      else delete assets[slot];
      props.onChange({ brand: { ...brand(), assets } });
    };
    const pick = (slot: WalletBrandAssetSlot, asset: RestaurantAsset | null) => {
      if (!asset) return setSlot(slot, undefined);
      props.onAssetUrl(asset.id, asset.url);
      setSlot(slot, {
        asset_id: asset.id,
        fit: slot === "artwork" ? "cover" : "contain",
      });
    };

    return () => (
      <div class="flex flex-col gap-4">
        <PanelSection
          title="Images"
          description="Optional. Pick or upload one high-resolution source per slot — every platform size is derived from it."
        >
          <div class="flex flex-col gap-3">
            {SLOTS.map((slot) => {
              const meta = SLOT_META[slot];
              const current = brand().assets[slot];
              return (
                <div
                  key={slot}
                  class="flex flex-col gap-3 p-3 rounded-lg border border-base-content/10 bg-base-200/30"
                >
                  <div class="flex items-center gap-3">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium">{meta.label}</span>
                        <span class="badge badge-ghost badge-xs font-mono">
                          {meta.size}
                        </span>
                      </div>
                      <p class="text-[11px] text-base-content/50">
                        {meta.hint}
                      </p>
                    </div>
                    <div class={meta.wide ? "w-36 shrink-0" : "w-20 shrink-0"}>
                      <AssetPicker
                        uploadsApi={
                          props.scope === "group" ? groupUploadsAPI : uploadsAPI
                        }
                        uploadType={UPLOAD_COLLECTION}
                        value={current?.asset_id ?? null}
                        aspect={meta.wide ? "wide" : "square"}
                        placeholder="Choose"
                        onSelect={(asset) => pick(slot, asset)}
                      />
                    </div>
                  </div>

                  {current && slot === "artwork" && (
                    <div class="grid grid-cols-2 gap-3">
                      <PanelRow
                        label={`Focus left–right — ${Math.round((current.focal_point?.x ?? 0.5) * 100)}%`}
                      >
                        <input
                          type="range"
                          min="0"
                          max="100"
                          class="range range-xs range-primary"
                          value={(current.focal_point?.x ?? 0.5) * 100}
                          onInput={(e) =>
                            setSlot(slot, {
                              ...current,
                              focal_point: {
                                x: Number((e.target as HTMLInputElement).value) / 100,
                                y: current.focal_point?.y ?? 0.5,
                              },
                            })
                          }
                        />
                      </PanelRow>
                      <PanelRow
                        label={`Focus top–bottom — ${Math.round((current.focal_point?.y ?? 0.5) * 100)}%`}
                      >
                        <input
                          type="range"
                          min="0"
                          max="100"
                          class="range range-xs range-primary"
                          value={(current.focal_point?.y ?? 0.5) * 100}
                          onInput={(e) =>
                            setSlot(slot, {
                              ...current,
                              focal_point: {
                                x: current.focal_point?.x ?? 0.5,
                                y: Number((e.target as HTMLInputElement).value) / 100,
                              },
                            })
                          }
                        />
                      </PanelRow>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </PanelSection>
      </div>
    );
  },
});
